import React, { useState, useRef } from 'react';
import {
  Email,
  EmailView,
  EmailEditKey,
} from 'react-scale/components/scalers/tasks/textcollection/WorkspaceComponents/EmailView';
import { Card, Icon, Modal, Typography } from '@scale/scaleui';
import { convertDate } from './helpers';

interface EmailPreviewProps {
  email: Email;
  viewed?: boolean;
  onView?: (key: string) => void;
  useEditor?: boolean | undefined;
  attachmentIndex?: number;
  metadataIndex?: number;
  canEdit?: boolean | undefined;
  handleEmailEdit?: (
    newValue: string,
    editKey: EmailEditKey,
    attachmentIndex?: number,
    metadataIndex?: number,
  ) => void;
}

// Consider adding established style names and then make it parametric for
// easier to change when for different customers
export const EmailPreview: React.FC<EmailPreviewProps> = props => {
  const [openEmail, setOpenEmail] = useState(false);
  const viewTimer = useRef<NodeJS.Timeout>();

  const VIEW_DURATION = 4000; // 4 seconds

  function startViewTimer() {
    viewTimer.current = setTimeout(() => {
      props.onView!(props.email.threadId);
    }, VIEW_DURATION);
  }

  const onCloseModal = () => {
    clearTimeout(viewTimer.current);
    setOpenEmail(false);
  };

  const {
    email,
    useEditor = false,
    attachmentIndex,
    metadataIndex,
    canEdit,
    handleEmailEdit,
  } = props;
  const sentences = email.messages[0]?.body.split(/(?<=[.!?])\s+/) || [];
  const previewLines =
    sentences.length >= 2
      ? sentences.slice(0, 3).join(' ').substring(0, 65) + '...'
      : email.messages[0]?.body || '';

  const uniqueLabels = new Set(email.messages.map(message => message.labels).flat());
  const important = uniqueLabels.has('IMPORTANT');
  const unread = uniqueLabels.has('UNREAD');

  return (
    <div>
      <Card
        className="mt-2 w-full shadow-[1px_1px_0px_2px_rgba(0,0,0,0.2)]"
        onClick={() => {
          setOpenEmail(true);
          startViewTimer();
        }}
      >
        <div style={{ width: '100%' }} className="flex">
          <div className="ml-2">
            <div className="flex justify-between">
              <div style={{ fontWeight: 'bolder' }} className="flex items-center">
                <Icon
                  size="md"
                  icon={['fas', unread ? 'envelope' : 'envelope-open']}
                  variant="neutral"
                />
                <Typography.H4>{email.subject}</Typography.H4>
                {email.messages.length > 1 && (
                  <Typography.Subtitle4 className="ml-2 mb-2">
                    {email.messages.length}
                  </Typography.Subtitle4>
                )}
                {important && (
                  <Icon
                    size="sm"
                    icon={['fas', 'exclamation']}
                    variant="danger"
                    className="bg-red-600"
                  />
                )}
              </div>
              {props.viewed && (
                <Icon size="md" icon={['fas', 'check-circle']} variant="success" className="ml-2" />
              )}
            </div>

            <Typography.Paragraph3>{previewLines}</Typography.Paragraph3>

            <div className="flex mt-4 justify-between">
              <Typography.Subtitle4>
                From: {email.messages[0]?.senderPerson.displayName || ''}
              </Typography.Subtitle4>
              <Typography.Subtitle4>
                Date: {email.messages[0]?.date ? convertDate(email.messages[0].date, true) : ''}
              </Typography.Subtitle4>
            </div>
          </div>
        </div>
      </Card>
      <Modal innerClassName="w-3/5 h-3/5" open={openEmail} onClose={onCloseModal}>
        <EmailView
          email={email}
          useEditor={useEditor}
          canEdit={canEdit}
          attachmentIndex={attachmentIndex}
          metadataIndex={metadataIndex}
          handleEmailEdit={handleEmailEdit}
        />
      </Modal>
    </div>
  );
};
